import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AssetConstellationMosaic } from "@/components/cycle/AssetConstellationMosaic";
import { CyclePulseHeader } from "@/components/cycle/CyclePulseHeader";
import { CycleHistoryBarStrip } from "@/components/cycle/CycleHistoryBarStrip";
import { MostMissedLeaderboard } from "@/components/cycle/MostMissedLeaderboard";
import { AssetHistoryDialog } from "@/components/cycle/AssetHistoryDialog";

const CATEGORIES = [
  { id: "cumulative", label: "All Assets" },
  { id: "road", label: "Roads" },
  { id: "osr", label: "OSR" },
  { id: "dumping_yard", label: "Dumping Yard" },
  { id: "project_office", label: "Project Office" },
  { id: "footpath", label: "Footpath" },
];

export default function CleaningCyclesParkPage() {
  const { parkId } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const category = sp.get("c") || "cumulative";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openAsset, setOpenAsset] = useState(null);
  const onCategoryChange = useCallback((id) => { sp.set("c", id); setSp(sp, { replace: true }); }, [sp, setSp]);

  useEffect(() => {
    if (!parkId) return;
    let cancel = false;
    setLoading(true);
    api.get(`/admin/cleaning-cycles/park/${parkId}`, { params: { category } })
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch((e) => { if (!cancel) { toast.error("Could not load park details"); console.error(e); setData(null); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [parkId, category]);

  const park = data?.park;
  const tiles = useMemo(() => data?.tiles || [], [data]);
  const cycle = data?.current_cycle;
  const history = data?.history || [];
  const stats = data?.stats || {};
  const mostMissed = data?.most_missed || [];
  const counts = useMemo(() => {
    const m = { complete: 0, in_progress: 0, not_started: 0, overdue: 0 };
    for (const t of tiles) m[t.state] = (m[t.state] || 0) + 1;
    return m;
  }, [tiles]);

  return (
    <AppShell
      title={park?.name ? `${park.name} · Park Pulse` : "Park Pulse"}
      subtitle={park?.code ? `Code ${park.code}${park.phase ? " · " + park.phase : ""}` : "Cleaning cycle health"}
      onRefresh={() => navigate(0)}
    >
      <div className="space-y-5" data-testid="cleaning-cycles-park-page">
        {/* Category tabs */}
        <Tabs value={category} onValueChange={onCategoryChange} data-testid="category-tabs">
          <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-slate-100/70 p-1">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.id} value={c.id} className="data-[state=active]:bg-white" data-testid={`category-tab-${c.id}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="grid gap-4 lg:grid-cols-12">
            <Skeleton className="h-32 lg:col-span-12 rounded-xl" />
            <Skeleton className="h-[400px] lg:col-span-8 rounded-xl" />
            <Skeleton className="h-[400px] lg:col-span-4 rounded-xl" />
          </div>
        ) : !data ? (
          <Card><CardContent className="p-10 text-center text-slate-500">No data available.</CardContent></Card>
        ) : (
          <>
            {/* Cycle Pulse Header */}
            <CyclePulseHeader cycle={cycle} stats={stats} sla={data.sla_target_days} />

            {/* Main grid */}
            <div className="grid gap-4 lg:grid-cols-12">
              {/* Mosaic */}
              <Card className="lg:col-span-8">
                <CardContent className="p-4 sm:p-5">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="font-display text-sm font-semibold">Asset Constellation</div>
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <Legend tone="emerald" label={`Complete · ${counts.complete}`} />
                      <Legend tone="amber"   label={`In progress · ${counts.in_progress}`} />
                      <Legend tone="rose"    label={`Overdue · ${counts.overdue}`} />
                      <Legend tone="slate"   label={`Not started · ${counts.not_started}`} />
                    </div>
                  </div>
                  <AssetConstellationMosaic tiles={tiles} onTileClick={(t) => setOpenAsset(t.asset_id)} />
                </CardContent>
              </Card>

              {/* Right column */}
              <div className="flex flex-col gap-4 lg:col-span-4">
                <MostMissedLeaderboard items={mostMissed} onPick={(t) => setOpenAsset(t.asset_id)} />
              </div>

              {/* Cycle history full width below */}
              <div className="lg:col-span-12">
                <CycleHistoryBarStrip history={history} stats={stats} />
              </div>
            </div>
          </>
        )}
      </div>
      <AssetHistoryDialog assetId={openAsset} open={!!openAsset} onOpenChange={(v) => !v && setOpenAsset(null)} />
    </AppShell>
  );
}

function Legend({ tone, label }) {
  const map = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    amber:   "border-amber-300 bg-amber-50 text-amber-800",
    rose:    "border-rose-300 bg-rose-50 text-rose-800",
    slate:   "border-slate-300 bg-slate-50 text-slate-700",
  };
  return <Badge variant="outline" className={map[tone]}>{label}</Badge>;
}
